import React, { useState } from "react";
import { CheckCircle } from "@mui/icons-material";
import { Alert, AlertTitle, Box, Paper, Typography } from "@mui/material";
import { groupBy } from "lodash";

import { NavigationButton } from "ui";

import { LayerStatus } from "fond/hooks/useGetImportLayers";
import { addImport } from "fond/redux/imports";
import { ProjectSubtype } from "fond/types";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { LinearProgressWithLabel, SvgIcon } from "fond/widgets";

import { fileNameToLayerId } from "./files";
import UploadS18Modal, { UploadFormData } from "./UploadS18Modal";

interface InputPanelProps {
  // Indicates whether the process is complete
  complete?: boolean;

  // Array of errors related to layer import status
  errors: LayerStatus[];

  // Indicates whether the process is currently in progress
  inProgress: boolean;

  // Flag indicating if the editing is read only
  isReadOnly?: boolean;

  // Project type
  // This is used to determine what files are required for upload
  type: ProjectSubtype;
}

const InputPanel: React.FC<InputPanelProps> = ({ complete, errors, inProgress, isReadOnly, type }: InputPanelProps) => {
  const versionId = useAppSelector((state) => state.project.versionId);
  const [showUpload, setShowUpload] = useState(false);
  const dispatch = useAppDispatch();

  const handleOnClick = () => {
    setShowUpload(true);
  };

  /**
   * On submit function called when the form is submitted and valid
   */
  const handleOnSubmit = async (values: UploadFormData) => {
    const { Files } = values;

    // Group the Tab files by their layer name
    const groupedFiles = groupBy(Files, (file) => file.name.split(".").at(0));

    // Dispatch the addImport action for each group of files. This will trigger the import process
    // for each layer (adding the data to the map).
    Object.keys(groupedFiles).forEach((key) => {
      const files = groupedFiles[key];
      // Get the layer key from the file name
      const layerKey = fileNameToLayerId(key, type);

      dispatch(
        addImport({
          versionId,
          layerKey,
          displayName: key,
          files,
        })
      );
    });

    setShowUpload(false);
  };

  return (
    <>
      <Typography variant="caption">Upload s18 design package in order to run cost to serve.</Typography>
      <NavigationButton
        data-testid="input-data-navigation"
        icon={<SvgIcon icon="polygon" />}
        title="s18 package"
        onClick={handleOnClick}
        sx={{ m: 0, mt: 1 }}
        disabled={inProgress || isReadOnly}
        checked={complete}
        rightElement={
          complete && (
            <Box display="flex" alignItems="center">
              <CheckCircle color="success" sx={{ fontSize: 18 }} />
            </Box>
          )
        }
      />

      {errors.length > 0 && (
        <Alert severity="error" variant="compact" sx={{ mt: 2 }}>
          <AlertTitle>Import failed</AlertTitle>
          This following layers failed to import:
          <ul style={{ margin: 0, paddingLeft: "1rem" }}>
            {errors.map((error) => (
              <li key={error.layer?.LayerKey}>{error.layer?.LayerKey || "Unknown error"}</li>
            ))}
          </ul>
        </Alert>
      )}

      {showUpload && (
        <UploadS18Modal
          type={type}
          onClose={() => {
            setShowUpload(false);
          }}
          onSubmit={handleOnSubmit}
        />
      )}

      {inProgress && (
        <Paper variant="outlined" sx={{ py: 2, px: 2.75, mt: 2 }}>
          <LinearProgressWithLabel color="primary" variant="indeterminate" title="Importing s18 package" subtext="Processing..." />
        </Paper>
      )}
    </>
  );
};

export default InputPanel;
